/**
 * Keep a tab on the organisation its screen is showing.
 *
 * The active organisation is one value per account (`users.active_organisation_id`),
 * read by the server on every request. Switching in one tab therefore moves
 * every other open tab, and every other device, at once — while their screens
 * still show the old organisation. Anything saved from one of those lands in
 * the new one.
 *
 * So each request names the organisation the tab was loaded in, and the server
 * refuses it with 409 `ORG_CHANGED` once that is no longer the active one. The
 * tab then reloads into the organisation the account is actually in and says
 * why. Tabs in the same browser are told straight away over a
 * BroadcastChannel; the 409 is what covers another device, another browser,
 * and an organisation changed for the user (a deactivation moves them on).
 */

export const ORG_HEADER = 'X-Organisation-Id';

/** Response header a backend sends once it accepts `ORG_HEADER`. */
export const ORG_GUARD_HEADER = 'x-org-guard';

const NOTICE_KEY = 'orgChangedNotice';
const CHANNEL_NAME = 'factree-org';

let tabOrgId: number | null = null;
let switchingToOrgId: number | null = null;
let serverAccepts = false;
let leaving = false;

/** The organisation this tab's screen is showing, or null when signed out. */
export function getTabOrgId(): number | null {
  return tabOrgId;
}

export function setTabOrgId(orgId: number | null | undefined): void {
  tabOrgId = orgId ?? null;
  switchingToOrgId = null;
}

/**
 * This tab is about to switch to `orgId` itself. A poll already in flight can
 * reach the server just after the switch and come back refused; that is this
 * tab's own doing and must not reload it. Cleared by `setTabOrgId`, or by
 * passing null when the switch fails.
 */
export function expectOrgSwitch(orgId: number | null): void {
  switchingToOrgId = orgId;
}

/**
 * A browser refuses any request carrying a header the server's CORS preflight
 * does not list, so sending this to a backend that predates it would fail
 * every call. It is sent only after the backend has said it takes it.
 */
export function noteServerAcceptsOrgHeader(): void {
  serverAccepts = true;
}

/**
 * Requests that must go through whatever the tab believes: choosing an
 * organisation (a stale tab switching back has the old one by definition),
 * and everything under /auth — sign-out, refresh, and `/auth/me`, which is
 * how a tab asks which organisation it is in.
 */
const UNCHECKED = [/\/users\/me\/switch-org(\?|$)/, /\/auth\//];

/** The value to send as `ORG_HEADER` for a request to `url`, or null for none. */
export function orgHeaderFor(url: string | undefined): string | null {
  if (!serverAccepts || tabOrgId === null) return null;
  if (url && UNCHECKED.some((pattern) => pattern.test(url))) return null;
  return String(tabOrgId);
}

/**
 * The account is in `activeOrgId`, and this tab is showing something else.
 * Leaves for the dashboard — the page it is on may be a record the new
 * organisation does not have — and leaves a note for the next load to show.
 *
 * Returns false, and does nothing, when the tab has already moved: a request
 * that was in flight while this tab itself switched comes back refused, and
 * that is not a reason to reload.
 */
export function leaveForActiveOrg(activeOrgId: number | null | undefined): boolean {
  if (leaving || tabOrgId === null) return false;
  const active = activeOrgId ?? null;
  if (active === tabOrgId) return false;
  if (active !== null && active === switchingToOrgId) return false;
  leaving = true;
  try {
    sessionStorage.setItem(NOTICE_KEY, '1');
  } catch {
    // Storage blocked (private mode): the tab still moves, without the note.
  }
  window.location.replace('/dashboard');
  return true;
}

/** True once, on the load that follows `leaveForActiveOrg`. */
export function takeOrgChangedNotice(): boolean {
  try {
    const pending = sessionStorage.getItem(NOTICE_KEY) === '1';
    if (pending) sessionStorage.removeItem(NOTICE_KEY);
    return pending;
  } catch {
    return false;
  }
}

// One channel object for both directions: a BroadcastChannel never delivers a
// message to the object that posted it, so the tab that switched is not told.
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    try {
      channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
    } catch {
      channel = null;
    }
  }
  return channel;
}

/** Tell this browser's other tabs that the account is now in `orgId`. */
export function announceOrgSwitch(orgId: number | null | undefined): void {
  try {
    getChannel()?.postMessage({ type: 'org-switched', orgId: orgId ?? null });
  } catch {
    // The 409 still catches it on the other tab's next request.
  }
}

/** Move this tab when another one switches. Returns the unsubscribe. */
export function followOrgSwitches(): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const handler = (event: MessageEvent) => {
    if (event.data?.type === 'org-switched') leaveForActiveOrg(event.data.orgId);
  };
  ch.addEventListener('message', handler);
  return () => ch.removeEventListener('message', handler);
}

/** Test seam: module state survives between tests in one file. */
export function resetOrgGuardForTests(): void {
  tabOrgId = null;
  switchingToOrgId = null;
  serverAccepts = false;
  leaving = false;
}
